import Decimal from 'decimal.js';
import { compact, groupBy } from 'lodash';

import { AIOnboardingAssetField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { getHoldingOwnerEntityID } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/holdings.utils';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getKgV2NodeIDFromSuggestion } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import {
  AssetValueV2OwnershipType,
  AugmentedCreateAssetV2Input,
  UpsertAssetToCurrentValuationInput,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

/**
 * Maps AI suggestions of kind ASSET to form data structure for the AIOnboarding
 * modal form.
 *
 * V2 suggestions use the explicit Entity edge written by the save layer.
 *
 * @param suggestions
 * @param household
 */
export function mapAssetsToFormData(
  suggestions: AiSuggestionForReviewCard[],
  household: AiOnboardingModal_HouseholdFragment
): AIOnboardingAssetField[] {
  const entities = getNodes(household.entities);

  return compact(
    suggestions.map((s) => {
      if (!s.asset) return null;

      // Parse asset value to Decimal if it exists
      let valuation = null;
      if (s.asset.assetValue) {
        try {
          valuation = new Decimal(s.asset.assetValue);
        } catch {
          // If parsing fails, leave as null
          valuation = null;
        }
      }

      const ownerId =
        s.entity?.id ||
        getHoldingOwnerEntityID(s.asset.owningEntityID || '', entities);

      return {
        _suggestionID: s.id,
        assetName: s.asset.assetTitle || '',
        ownerId,
        assetCategoryId: s.asset.assetCategoryID || '',
        valuation,
      };
    })
  );
}

export function getEmptyAssetField(): AIOnboardingAssetField {
  return {
    _suggestionID: undefined,
    assetName: '',
    ownerId: '',
    assetCategoryId: '',
    valuation: null,
  };
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Maps the asset fields from the form data to the input structure to create
 * assets using the upsertAssetToCurrentValuation mutation.
 *
 * @param assets
 * @param household
 * @param suggestionsByID
 */
export function mapAssetsToInput(
  assets: AIOnboardingAssetField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): UpsertAssetToCurrentValuationInput[] {
  // Filter out assets without valid owner IDs
  const validAssets = assets.filter(
    (asset) => asset.ownerId && asset.valuation && asset.assetCategoryId
  );

  if (validAssets.length === 0) {
    return [];
  }

  // Group assets by entity owner ID
  const assetsByEntityId = groupBy(validAssets, 'ownerId');

  // For each entity with assets, create an UpsertAssetToCurrentValuation input
  return compact(
    Object.entries(assetsByEntityId).map(([entityId, entityAssets]) => {
      // Create asset inputs for this entity
      const assetInputs: AugmentedCreateAssetV2Input[] = entityAssets.map(
        (asset) => {
          const suggestion = asset._suggestionID
            ? suggestionsByID[asset._suggestionID]
            : undefined;
          const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);

          return {
            create: {
              classID: asset.assetCategoryId,
              displayName: asset.assetName,
              householdID: household.id,
              kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
            },
            withAssetValue: {
              create: {
                ownedValue: asset.valuation!,
                ownershipType: AssetValueV2OwnershipType.ValueBased,
                householdID: household.id,
              },
            },
          };
        }
      );

      return {
        entityID: entityId,
        input: assetInputs,
      };
    })
  );
}
